import { clinicalWallTime, clinicalOffset, clinicalTimeZone, offsetSuffix, captureClinicalNow, resolveClinicalTime } from './clinicalTime';

const fields = new Set([
  'pushingstart', 'crowning', 'headborntime', 'bodyborntime', 'firstbreathtime', 'birthdatetime',
  'earlylaborstart', 'activelaborstart', 'secondstagestart', 'thirdstagestart', 'placentadeliverytime',
  'deliveryassistanceinitiated', 'assistanceinitiatedtime', 'dischargedatetime', 'buttocksvisible',
  'buttocksborn', 'trunktoumbilicus', 'trunktoaxillae', 'armsdelivered', 'headvisible', 'feetvisible',
  'feetdelivered', 'buttocksdelivered', 'timestamp', 'occurredat', 'stoppedat', 'timeresuscitated',
  'deliverytime', 'cordclampedtime', 'cordcuttime', 'cordcutdatetime', 'cordbloodcollectedtime',
  'firstlatchtime', 'firstlatch1time', 'firstlatch2time', 'newbornexamtime', 'vitaminktime', 'eyeointmenttime'
  , 'assessmentdatetime', 'membranerupturedatetime'
]);
const medicationClock = key => ['vitaminktime', 'eyeointmenttime'].includes(key.toLowerCase());
const matchesClock = (key, item, info) => clinicalWallTime(item) === info.localDateTime
  || (medicationClock(key) && typeof item === 'string' && item === info.localDateTime?.slice(11, 19));
const canonical = key => key[0].toLowerCase() + key.slice(1);
const pathKey = path => path.filter(part => !['criticaltimes', 'breechtimes'].includes(String(part).toLowerCase())).map(part => canonical(String(part))).join('.');
const read = (value, key) => value?.[Object.keys(value || {}).find(name => name.toLowerCase() === key.toLowerCase())];
const getMetadata = value => {
  const metadata = read(value, 'clinicalTimeMetadata');
  return metadata && Object.fromEntries(Object.entries(metadata).map(([key, info]) => [pathKey(key.split('.')), info && {
    localDateTime: read(info, 'localDateTime'), utcOffsetMinutes: read(info, 'utcOffsetMinutes'), timeZoneId: read(info, 'timeZoneId')
  }]));
};

const primaryClockFields = new Set([
  'pushingstart', 'crowning', 'headborntime', 'bodyborntime', 'firstbreathtime', 'birthdatetime',
  'earlylaborstart', 'activelaborstart', 'secondstagestart', 'thirdstagestart', 'placentadeliverytime',
  'deliveryassistanceinitiated', 'assistanceinitiatedtime', 'dischargedatetime', 'buttocksvisible',
  'buttocksborn', 'trunktoumbilicus', 'trunktoaxillae', 'armsdelivered', 'headvisible', 'feetvisible',
  'feetdelivered', 'buttocksdelivered'
]);
const write = (value, key, item) => {
  const existing = Object.keys(value).find(name => name.toLowerCase() === key.toLowerCase());
  value[existing || key] = item;
};
const clockFields = event => [event, read(event, 'criticalTimes'), read(event, 'breechTimes')].filter(Boolean);
const alignBirthDateTime = event => {
  if (!event) return;
  const body = clockFields(event).map(part => read(part, 'bodyBornTime')).find(Boolean);
  if (body) {
    write(event, 'birthDateTime', body);
    const metadata = getMetadata(event);
    if (metadata) write(event, 'clinicalTimeMetadata', { ...metadata, birthDateTime: metadata.bodyBornTime || null });
  }
};

// A singleton has two editor views of one stored birth event. Match the API's
// ownership: maternal clocks come from the primary view, newborn exam from baby.
// Run this for both online requests and the durable offline payload.
const alignEpisodeClocks = episode => {
  const primary = read(episode, 'primaryBirthEvent');
  const babies = read(episode, 'babies');
  alignBirthDateTime(primary);
  if (!Array.isArray(babies)) return;
  babies.forEach(entry => alignBirthDateTime(read(entry, 'birthEvent')));
  const baby = babies.length === 1 && read(babies[0], 'birthEvent');
  if (!primary || !baby || read(read(episode, 'pregnancy'), 'isTwins') || read(read(babies[0], 'baby'), 'isTwin')) return;
  if (read(primary, 'referenceId') && read(baby, 'referenceId')
      && read(primary, 'referenceId') !== read(baby, 'referenceId')) return;
  // Newborn medications belong to the newborn editor, including explicit clears.
  for (const key of ['vitaminKTime', 'eyeOintmentTime']) {
    if (read(baby, key) !== undefined) {
      write(primary, key, read(baby, key));
      const primaryMetadata = getMetadata(primary) || {};
      primaryMetadata[key] = getMetadata(baby)?.[key] ?? null;
      write(primary, 'clinicalTimeMetadata', primaryMetadata);
    }
  }
  const clocks = new Map();
  clockFields(primary).forEach(part => Object.entries(part).forEach(([key, item]) => {
    if (primaryClockFields.has(key.toLowerCase())) clocks.set(key.toLowerCase(), [key, item]);
  }));
  clockFields(baby).forEach(part => Object.keys(part).forEach(key => {
    if (clocks.has(key.toLowerCase())) part[key] = clocks.get(key.toLowerCase())[1];
  }));
  for (const [key, item] of clocks.values()) {
    if (!clockFields(baby).some(part => read(part, key) !== undefined)) write(baby, key, item);
  }
  const metadata = { ...(getMetadata(baby) || {}) };
  for (const key of Object.keys(metadata)) if (clocks.has(key.toLowerCase())) metadata[key] = null;
  for (const [key, info] of Object.entries(getMetadata(primary) || {})) {
    if (clocks.has(key.toLowerCase())) metadata[key] = info;
  }
  if (Object.keys(metadata).length) write(baby, 'clinicalTimeMetadata', metadata);
};

const occurrenceLabel = parts => {
  const field = String(parts[parts.length - 1]).replace(/([a-z])([A-Z])/g, '$1 $2');
  const babyIndex = parts.findIndex(part => String(part).toLowerCase() === 'babies');
  const prefix = babyIndex >= 0 ? `Baby ${Number(parts[babyIndex + 1]) + 1}: ` : '';
  return prefix + field.charAt(0).toUpperCase() + field.slice(1);
};

// Existing local clock fields remain unchanged on the wire. The companion metadata
// carries the occurrence, and participates in the same durable offline mutation.
export function encodeClinicalTimes(input, timeZone = clinicalTimeZone()) {
  if (!input || typeof input !== 'object') return input;
  if (Array.isArray(input)) return input.map(item => encodeClinicalTimes(item, timeZone));
  const output = JSON.parse(JSON.stringify(input));
  alignEpisodeClocks(output);
  for (const key of Object.keys(output)) {
    if (['primarybirthevent', 'birthevent', 'resuscitationepisode'].includes(key.toLowerCase())) output[key] = encodeClinicalTimes(output[key], timeZone);
    if (key.toLowerCase() === 'babies' && Array.isArray(output[key])) output[key] = output[key].map(entry => encodeClinicalTimes(entry, timeZone));
  }
  const metadata = { ...(getMetadata(output) || {}) };
  const walk = (value, path = []) => {
    if (!value || typeof value !== 'object') return;
    for (const [key, item] of Object.entries(value)) {
      if (['clinicaltimemetadata', 'notes', 'primarybirthevent', 'birthevent', 'babies', 'resuscitationepisode'].includes(key.toLowerCase())) continue;
      const parts = [...path, key];
      if (typeof item === 'string' && fields.has(key.toLowerCase()) && clinicalWallTime(item)) {
        const name = pathKey(parts), offset = /[+-]\d{2}:\d{2}$/.test(item) ? clinicalOffset(item) : null;
        if (offset !== null) {
          const prior = metadata[name];
          const candidates = [prior?.localDateTime === clinicalWallTime(item) && prior?.utcOffsetMinutes === offset ? prior.timeZoneId : null,
            timeZone, ...Object.values(metadata).map(info => info?.timeZoneId)];
          let zone = candidates.find(candidate => candidate && clinicalOffset(captureClinicalNow(candidate, new Date(item))) === offset) || null;
          // An imported explicit offset remains valid even without a known IANA zone.
          if (zone && clinicalOffset(captureClinicalNow(zone, new Date(item))) !== offset) zone = null;
          metadata[name] = { localDateTime: clinicalWallTime(item), utcOffsetMinutes: offset, timeZoneId: zone };
          value[key] = medicationClock(key) ? clinicalWallTime(item).slice(11, 19) : clinicalWallTime(item);
        } else {
          if (metadata[name] && metadata[name].localDateTime !== clinicalWallTime(item)) metadata[name] = null;
          if (item.endsWith('Z')) value[key] = clinicalWallTime(item);
        }
      } else if (typeof item === 'string' && /^[\[{]/.test(item)) {
        try { const parsed = JSON.parse(item); walk(parsed, parts); value[key] = JSON.stringify(parsed); } catch { /* Non-JSON notes stay intact. */ }
      } else if (item && typeof item === 'object') {
        // Resuscitation/hemorrhage events own their metadata. Postpartum rows
        // (timestamp/deliveryTime) remain indexed fields of the assessment.
        if (getMetadata(item) || read(item, 'occurredAt') !== undefined) value[key] = encodeClinicalTimes(item, timeZone);
        else walk(item, parts);
      }
      else if ((item === null || item === '') && metadata[pathKey(parts)]) metadata[pathKey(parts)] = null;
    }
  };
  walk(output);
  // Removing/reordering an assessment row must not retain its former occurrence.
  // Explicit timestamps above replace matching paths before stale entries clear.
  for (const [key, info] of Object.entries(metadata)) {
    if (!info || key === 'recordedAt') continue;
    const find = (node, parts) => {
      if (typeof node === 'string' && /^[\[{]/.test(node)) { try { node = JSON.parse(node); } catch { return undefined; } }
      return parts.length ? find(read(node, parts[0]), parts.slice(1)) : node;
    };
    const item = find(output, key.split('.')) ?? find(read(output, 'criticalTimes'), key.split('.')) ?? find(read(output, 'breechTimes'), key.split('.'));
    if (item !== undefined && !matchesClock(key, item, info)) metadata[key] = null;
    if (key.includes('.') && item === undefined) metadata[key] = null;
  }
  if (Object.keys(metadata).length) {
    delete output.ClinicalTimeMetadata;
    output.clinicalTimeMetadata = metadata;
  }
  return output;
}

export function decodeClinicalTimes(input) {
  if (!input || typeof input !== 'object') return input;
  if (Array.isArray(input)) return input.map(decodeClinicalTimes);
  const output = Object.fromEntries(Object.entries(input).map(([key, value]) => [key, key.toLowerCase() === 'clinicaltimemetadata' ? value : decodeClinicalTimes(value)]));
  const metadata = getMetadata(output);
  if (!metadata) return output;
  const walk = (value, path = []) => {
    if (!value || typeof value !== 'object') return;
    for (const [key, item] of Object.entries(value)) {
      if (['clinicaltimemetadata', 'primarybirthevent', 'birthevent', 'babies', 'resuscitationepisode'].includes(key.toLowerCase())) continue;
      const parts = [...path, key], info = metadata[pathKey(parts)];
      if (info && typeof item === 'string' && matchesClock(key, item, info))
        value[key] = `${read(info, 'localDateTime')}${offsetSuffix(read(info, 'utcOffsetMinutes'))}`;
      else if (typeof item === 'string' && /^[\[{]/.test(item)) {
        try { const parsed = JSON.parse(item); walk(parsed, parts); value[key] = JSON.stringify(parsed); } catch { /* Non-JSON notes stay intact. */ }
      } else if (item && typeof item === 'object' && !getMetadata(item)) walk(item, parts);
    }
  };
  walk(output);
  return output;
}

const askOccurrence = detail => new Promise((resolve, reject) => {
  const request = { ...detail, resolve, handled: false };
  window.dispatchEvent(new CustomEvent('vela:resolve-clinical-time', { detail: request }));
  if (!request.handled) reject(new Error('Confirm whether this time occurred before or after the clock change.'));
});

export async function prepareClinicalTimes(input, base, choose = askOccurrence, timeZone = clinicalTimeZone()) {
  const output = decodeClinicalTimes(encodeClinicalTimes(input, timeZone));
  const previous = decodeClinicalTimes(encodeClinicalTimes(base, timeZone));
  const hasOccurrences = /T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}/.test(JSON.stringify(output));
  const resolvedFields = new Map();
  const walk = async (value, prior, path = [], inheritedZone = timeZone) => {
    if (!value || typeof value !== 'object') return;
    const birthAlias = read(value, 'birthDateTime') !== undefined
      && clockFields(value).some(part => read(part, 'bodyBornTime'));
    const eventZone = Object.values(getMetadata(value) || {}).find(info => info?.timeZoneId)?.timeZoneId || inheritedZone;
    for (const [key, item] of Object.entries(value)) {
      if (['clinicaltimemetadata', 'notes'].includes(key.toLowerCase())) continue;
      if (birthAlias && key.toLowerCase() === 'birthdatetime') continue;
      const parts = [...path, key];
      const old = read(prior, key);
      if (typeof item === 'string' && fields.has(key.toLowerCase()) && clinicalWallTime(item)) {
        // Flat and nested critical-time representations refer to the same fact.
        const occurrenceKey = `${pathKey(parts)}:${clinicalWallTime(item)}`;
        if (resolvedFields.has(occurrenceKey)) { value[key] = resolvedFields.get(occurrenceKey); continue; }
        if (clinicalOffset(item) !== null) continue;
        if (clinicalWallTime(item) === clinicalWallTime(old) && (!hasOccurrences || clinicalOffset(old) !== null)) {
          // Keep an unchanged legacy value unknown; never infer its occurrence.
          value[key] = old;
          continue;
        }
        const resolution = resolveClinicalTime(item, eventZone);
        if (resolution.value) value[key] = resolution.value;
        else if (resolution.candidates.length > 1) {
          const label = occurrenceLabel(parts);
          const selected = await choose({ label, wall: clinicalWallTime(item), timeZone: eventZone, candidates: resolution.candidates });
          if (!resolution.candidates.includes(selected)) throw new Error('Time confirmation was cancelled. Your changes remain in the editor.');
          value[key] = selected;
        } else throw new Error(resolution.error);
        resolvedFields.set(occurrenceKey, value[key]);
      } else if (typeof item === 'string' && /^[\[{]/.test(item)) {
        let parsed;
        try { parsed = JSON.parse(item); } catch { continue; }
        let oldParsed = old;
        try { if (typeof old === 'string') oldParsed = JSON.parse(old); } catch { oldParsed = undefined; }
        await walk(parsed, oldParsed, parts, eventZone);
        value[key] = JSON.stringify(parsed);
      } else if (item && typeof item === 'object') await walk(item, old, parts, eventZone);
    }
    if (birthAlias) alignBirthDateTime(value);
  };
  const primaryKey = Object.keys(output || {}).find(key => key.toLowerCase() === 'primarybirthevent');
  if (primaryKey) {
    // Resolve the authoritative view first, then mirror its confirmed choices.
    const primary = output[primaryKey];
    await walk(primary, read(previous, primaryKey), [primaryKey]);
    alignEpisodeClocks(output);
  }
  await walk(output, previous);
  return encodeClinicalTimes(output, timeZone);
}
